<template>
  <v-card class="mx-auto" max-width="460" rounded="lg" elevation="6">
    <v-toolbar dense tile flat color="grey lighten-3">
      <v-toolbar-title class="align-center d-flex text-subtitle-1">
        <v-icon class="mr-2" color="primary">mdi-shield-lock</v-icon>
        Two-Factor Verification
      </v-toolbar-title>
    </v-toolbar>
    <v-divider />

    <v-img
      src="/assets/SDLOGO_STACKED.png"
      max-width="190"
      class="mx-auto mt-6 mb-2"
    />

    <v-row justify="center" class="mt-0">
      <v-col md="12">
        <v-alert
          :value="alert.show"
          :type="alert.type"
          :icon="alertIcon"
          text
          dense
          transition="fade-transition"
          class="mx-4 mt-2 mb-0 caption"
          >{{ alert.message }}</v-alert
        >

        <v-card-text class="px-4 pt-2 pb-0">
          <div class="text-subtitle-2 text-center grey--text text--darken-2">
            Enter the 6-digit code sent to
          </div>
          <div class="text-center font-weight-medium mb-4">
            {{ contactMethodDetails }}
          </div>

          <v-otp-input
            v-model="otpCode"
            length="6"
            type="number"
            @finish="verifyOtp"
          />

          <div v-if="showResendOption" class="text-center mt-3">
            <span v-if="countdownActive" class="caption grey--text">
              Resend code in {{ formatTime(countdown) }}
            </span>
            <v-btn
              v-else
              text
              small
              color="primary"
              class="text-none"
              :disabled="resendDisabled"
              @click="resendOtp(null)"
            >
              Resend code
            </v-btn>
          </div>

          <!-- Method picker — shown when 2+ contact methods registered -->
          <div v-if="contactMethods.length >= 2" class="mt-4">
            <div class="text-center">
              <v-btn
                text
                x-small
                color="primary"
                class="text-none"
                @click="showMethodPicker = !showMethodPicker"
              >
                Use a different method
                <v-icon x-small right>
                  {{ showMethodPicker ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
              </v-btn>
            </div>
            <v-expand-transition>
              <div v-if="showMethodPicker" class="mt-2">
                <v-sheet
                  v-for="cm in contactMethods"
                  :key="cm.id"
                  outlined
                  rounded="lg"
                  class="method-option d-flex align-center pa-3 mb-2"
                  :class="{
                    'method-option--selected': cm.id === activeContactMethodId,
                  }"
                  @click="switchMethod(cm)"
                >
                  <v-avatar
                    size="36"
                    class="mr-3"
                    :color="
                      cm.id === activeContactMethodId
                        ? 'primary'
                        : 'grey lighten-3'
                    "
                  >
                    <v-icon
                      small
                      :color="
                        cm.id === activeContactMethodId
                          ? 'white'
                          : 'grey darken-1'
                      "
                    >
                      {{ methodIcon(cm.type) }}
                    </v-icon>
                  </v-avatar>
                  <div class="flex-grow-1" style="min-width: 0">
                    <div class="body-2 text-truncate">{{ cm.contact }}</div>
                  </div>
                  <v-icon
                    v-if="cm.id === activeContactMethodId"
                    small
                    color="primary"
                  >
                    mdi-check
                  </v-icon>
                </v-sheet>
              </div>
            </v-expand-transition>
          </div>
        </v-card-text>

        <v-divider class="mt-4" />
        <v-card-actions class="px-4 py-3">
          <v-btn
            text
            class="text-none"
            :disabled="loading"
            @click="resetLoginState"
          >
            <v-icon left small>mdi-arrow-left</v-icon>
            Back to login
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            depressed
            :loading="loading"
            :disabled="!otpCode"
            @click="verifyOtp"
          >
            Verify
          </v-btn>
        </v-card-actions>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { setToken } from "../../apollo-client/apollo-token";
import { titleCase } from "@tod-ui/helpers/strings";
import useUser from "./useUser";
import {
  GET_CONTACT_METHODS,
  VALIDATE_OTP_AND_LOGIN,
  RESEND_OTP,
} from "./graphql";
import useAlerts from "@tod-ui/composables/useAlerts";

const TFA_VERIFY_TOKEN_KEY = "tfa_verify_token";
const TFA_METHOD_KEY = "tfa_method";

export default {
  name: "TwoFactorVerification",
  props: {
    token: {
      type: String,
      default: null,
    },
    twoFactorMethod: {
      type: String,
      default: null,
    },
  },
  setup() {
    const { completeLogin } = useUser();
    return { completeLogin };
  },
  data() {
    const { localAlerts, addLocalAlert, clearAllLocalAlerts } = useAlerts();

    function showError(message) {
      addLocalAlert({ message, timeout: 3 });
    }
    return {
      contactMethods: [],
      contactMethodDetails: "your registered contact method",
      otpCode: "",
      loading: false,
      error: null,
      showResendOption: false,
      countdownActive: false,
      countdown: 30,
      countdownTimer: null,
      resendDisabled: false,
      effectiveToken: null,
      effectiveTwoFactorMethod: null,
      activeContactMethodId: null,
      showMethodPicker: false,
      localAlerts,
      addLocalAlert,
      clearAllLocalAlerts,
      showError,
      alert: {
        show: false,
        type: null,
        message: null,
      },
    };
  },
  computed: {
    // Lighter outline icons for the tonal alert (matches the 2FA setup screen).
    alertIcon() {
      return (
        {
          success: "mdi-check-circle-outline",
          error: "mdi-alert-circle-outline",
          warning: "mdi-alert-outline",
          info: "mdi-information-outline",
        }[this.alert.type] || "mdi-information-outline"
      );
    },
  },
  created() {
    if (this.token) {
      localStorage.setItem(TFA_VERIFY_TOKEN_KEY, this.token);
    }
    if (this.twoFactorMethod) {
      localStorage.setItem(TFA_METHOD_KEY, this.twoFactorMethod);
    }

    this.effectiveToken =
      this.token || localStorage.getItem(TFA_VERIFY_TOKEN_KEY);
    this.effectiveTwoFactorMethod =
      this.twoFactorMethod || localStorage.getItem(TFA_METHOD_KEY);

    if (!this.effectiveToken) {
      this.error = "Authentication required. Please log in again.";
      setTimeout(() => {
        this.resetLoginState();
      }, 3000);
      return;
    }

    this.fetchContactMethods();
  },
  beforeDestroy() {
    this.clearCountdownTimer();
  },
  methods: {
    async fetchContactMethods() {
      this.loading = true;
      try {
        const result = await this.$apollo.query({
          query: GET_CONTACT_METHODS,
          variables: {
            token: this.effectiveToken,
          },
          fetchPolicy: "network-only",
        });

        if (result.data?.userMContactMethods) {
          this.contactMethods = result.data.userMContactMethods;

          if (this.effectiveTwoFactorMethod) {
            const selectedMethod = this.contactMethods.find(
              (method) => method.id === this.effectiveTwoFactorMethod
            );

            if (selectedMethod) {
              this.contactMethodDetails = `${selectedMethod.contact}`;
              this.activeContactMethodId = selectedMethod.id;
            }
          } else if (this.contactMethods.length > 0) {
            this.activeContactMethodId = this.contactMethods[0].id;
          }
          // NOTE: this only *fetches* the contact methods — the OTP was already
          // sent by the backend at login. Don't claim "OTP sent successfully"
          // here, or it falsely appears on every page refresh. That success
          // toast belongs to resendOtp (an actual re-send). The header already
          // tells the user where the code was sent.
          this.showResendOption = true;
          this.startCountdown();
        }
      } catch (error) {
        this.displayError("Failed to load contact information");
        setTimeout(() => {
          this.resetLoginState();
        }, 3000);
      } finally {
        this.loading = false;
      }
    },
    async verifyOtp() {
      this.loading = true;
      this.error = null;
      try {
        const response = await this.$apollo.mutate({
          mutation: VALIDATE_OTP_AND_LOGIN,
          variables: {
            token: this.effectiveToken,
            otp: this.otpCode,
          },
        });

        if (response.data?.validateOtpAndLogin?.token) {
          const token = response.data.validateOtpAndLogin.token;
          const user = response.data.validateOtpAndLogin.user;

          this.clearTfaLocalStorage();

          setToken(token);
          // This user just cleared 2FA — keep the header ribbon logic correct.
          user.twoFactorRequired = true;
          // Route through the shared landing routine so 2FA logins get the same
          // role-aware destination, $lastRoute restore, and scope/facility/chatbot
          // initialization as a password login (completeLogin calls setCurrentUser).
          await this.completeLogin(this.$apollo, user);
        } else {
          this.displayError("Verification failed");
        }
      } catch (error) {
        this.displayError("Invalid verification code");
        this.showResendOption = true;
      } finally {
        this.loading = false;
        setTimeout(this.clearAllAlerts, 5000);
      }
    },
    async resendOtp(contactMethodId) {
      this.loading = true;
      this.error = null;
      try {
        await this.$apollo.mutate({
          mutation: RESEND_OTP,
          variables: {
            token: this.effectiveToken,
            contactMethodId: contactMethodId || undefined,
          },
        });

        // Persist the method the code was actually sent to, so a page refresh
        // restores the switched method instead of snapping back to the first.
        if (contactMethodId) {
          this.effectiveTwoFactorMethod = contactMethodId;
          localStorage.setItem(TFA_METHOD_KEY, contactMethodId);
        }

        this.startCountdown();
        this.otpCode = "";
        this.alert = {
          message: `OTP sent successfully to ${this.contactMethodDetails}`,
          type: "success",
          show: true,
        };
        setTimeout(this.clearAllAlerts, 5000);
      } catch (error) {
        // Map the raw backend codes to readable copy (matches the backoffice
        // 2FA page), and read the message safely so a missing graphQLErrors
        // array can't throw.
        const rawMessage =
          error?.graphQLErrors?.[0]?.message || error?.message || "";
        let message = "Failed to resend the code. Please try again.";
        if (rawMessage.includes("resend_too_soon")) {
          message = "Please wait a moment before requesting another code.";
        } else if (rawMessage.includes("max_otp_retries_reached")) {
          message = "Maximum attempts reached. Please sign in again.";
        }
        // Set the alert directly (not via displayError) so the copy isn't
        // title-cased into "Resend Too Soon" / "Max Otp Retries Reached".
        this.error = message;
        this.showError(message);
        this.alert = { message, type: "error", show: true };

        if (rawMessage.includes("max_otp_retries_reached")) {
          this.clearTfaLocalStorage();
          setTimeout(() => {
            this.resetLoginState();
          }, 3000);
        }
      } finally {
        this.loading = false;
        setTimeout(this.clearAllAlerts, 5000);
      }
    },

    async switchMethod(cm) {
      this.showMethodPicker = false;
      // Already the active method — don't re-send (would hit the resend throttle).
      if (cm.id === this.activeContactMethodId) return;
      this.activeContactMethodId = cm.id;
      // cm.contact is already masked server-side by userMContactMethods
      this.contactMethodDetails = cm.contact;
      await this.resendOtp(cm.id);
    },
    methodIcon(type) {
      if (type === "email") return "mdi-email-outline";
      if (type === "mobile") return "mdi-cellphone";
      return "mdi-phone-outline";
    },
    clearTfaLocalStorage() {
      localStorage.removeItem(TFA_VERIFY_TOKEN_KEY);
      localStorage.removeItem(TFA_METHOD_KEY);
    },
    startCountdown() {
      this.countdown = 30;
      this.countdownActive = true;
      this.resendDisabled = true;

      this.clearCountdownTimer();

      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          this.countdownActive = false;
          this.resendDisabled = false;
          this.clearCountdownTimer();
        }
      }, 1000);
    },
    clearCountdownTimer() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    },
    displayError(message) {
      this.error = message;
      const formattedText = titleCase(message);
      this.showError(formattedText);

      this.alert = {
        message: formattedText,
        type: "error",
        show: true,
      };
    },

    clearAllAlerts() {
      this.clearAlert();
      this.clearAllLocalAlerts();
    },

    clearAlert() {
      this.alert = {
        show: false,
        type: null,
        message: null,
      };
    },

    onAlertDismiss() {
      this.clearAlert();
    },
    resetLoginState() {
      localStorage.removeItem("tfa_verify_token");
      localStorage.removeItem("tfa_method");
      localStorage.removeItem("apollo-token");
      window.location.href = "/login";
    },
    titleCase,
  },
};
</script>

<style scoped>
/* Selectable contact-method cards (matches the 2FA setup screen). */
.method-option {
  cursor: pointer;
  border-color: rgba(0, 0, 0, 0.16) !important;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.method-option:hover {
  border-color: rgba(0, 0, 0, 0.32) !important;
  background-color: rgba(0, 0, 0, 0.02);
}

.method-option--selected {
  border-color: var(--v-primary-base) !important;
  background-color: rgba(var(--v-primary-base-rgb, 45, 46, 117), 0.06);
}
</style>
